import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Star, ChevronLeft } from "lucide-react";
import { REVIEWS, PRODUCTS_FOR_REVIEWS } from "../data/reviews";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Button } from "../components/ui/button";

const RatingBar = ({ value, total, filled }) => {
  const pct = total ? (filled / total) * 100 : 0;
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-10 shrink-0 text-[#2C3E50]/80">{value}★</span>
      <div className="flex-1 h-2 rounded-full bg-[#F39C12]/15 overflow-hidden">
        <div
          className="h-full bg-[#F39C12]"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-10 shrink-0 text-right text-xs text-[#7F8C8D]">
        {filled}
      </span>
    </div>
  );
};

const ReviewCard = ({ r, idx }) => (
  <article
    data-testid={`review-card-${idx}`}
    className="bg-white border border-[#F39C12]/15 rounded-2xl p-6"
  >
    <div className="flex items-center gap-1 mb-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${
            i < r.rating
              ? "fill-[#F39C12] text-[#F39C12]"
              : "text-[#F39C12]/25"
          }`}
        />
      ))}
    </div>
    <p className="text-[#2C3E50] text-base leading-relaxed">"{r.quote}"</p>
    <div className="mt-4 pt-3 border-t border-[#F39C12]/15 flex items-center justify-between text-sm gap-3">
      <div>
        <p className="font-display font-semibold text-[#2C3E50]">{r.name}</p>
        <p className="text-xs text-[#7F8C8D]">
          {r.location || "Verified buyer"}
          {r.created_at
            ? ` · ${new Date(r.created_at).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}`
            : ""}
        </p>
      </div>
      <span className="text-[10px] uppercase tracking-widest text-[#F39C12] font-semibold text-right shrink-0">
        {r.product}
      </span>
    </div>
  </article>
);

const Reviews = () => {
  const [selected, setSelected] = useState("all");

  const filtered = useMemo(() => {
    if (selected === "all") return REVIEWS;
    const productName = PRODUCTS_FOR_REVIEWS.find((p) => p.id === selected)?.name;
    return REVIEWS.filter((r) => r.product === productName);
  }, [selected]);

  const total = filtered.length;
  const avg = useMemo(() => {
    if (!total) return 0;
    return (filtered.reduce((s, r) => s + r.rating, 0) / total).toFixed(1);
  }, [filtered, total]);

  const breakdown = useMemo(() => {
    const counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    filtered.forEach((r) => (counts[r.rating] = (counts[r.rating] || 0) + 1));
    return counts;
  }, [filtered]);

  return (
    <div className="font-body">
      <section className="bg-[#FBF7F0] py-12 border-b border-[#F39C12]/15">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm text-[#D63031] font-medium mb-4 hover:underline"
            data-testid="reviews-back-link"
          >
            <ChevronLeft className="w-4 h-4" /> Back to home
          </Link>
          <p className="text-sm uppercase tracking-[0.2em] text-[#D63031] font-semibold mb-2">
            Customer reviews
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#2C3E50]">
            What our customers say
          </h1>

          <div className="mt-6 flex flex-col sm:flex-row sm:items-end gap-6">
            <div>
              <div className="flex items-baseline gap-2">
                <span className="font-display text-5xl font-bold text-[#D63031]">
                  {avg}
                </span>
                <span className="text-[#7F8C8D]">/ 5</span>
              </div>
              <div className="flex items-center gap-0.5 mt-1">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star
                    key={i}
                    className={`w-5 h-5 ${
                      i < Math.round(avg)
                        ? "fill-[#F39C12] text-[#F39C12]"
                        : "text-[#F39C12]/25"
                    }`}
                  />
                ))}
              </div>
              <p className="text-sm text-[#7F8C8D] mt-1">
                Based on {total} customer reviews
              </p>
            </div>

            <div className="flex-1 max-w-md space-y-1.5">
              {[5, 4, 3, 2, 1].map((v) => (
                <RatingBar
                  key={v}
                  value={v}
                  total={total}
                  filled={breakdown[v] || 0}
                />
              ))}
            </div>
          </div>

          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <span className="text-sm text-[#2C3E50] font-medium">
              Filter by product:
            </span>
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger
                data-testid="reviews-product-select"
                className="w-56 bg-white border-[#F39C12]/30"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All products</SelectItem>
                {PRODUCTS_FOR_REVIEWS.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {filtered.length === 0 ? (
            <p className="text-center text-[#7F8C8D] py-16">
              No reviews yet for this product.
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((r, idx) => (
                <ReviewCard key={idx} r={r} idx={idx} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-12 bg-[#D63031] text-white text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="font-display text-3xl font-bold">
            Ready to try it yourself?
          </h2>
          <Link
            to="/#product"
            data-testid="reviews-cta-buy"
            className="mt-6 inline-flex items-center rounded-full h-12 px-7 bg-white text-[#D63031] font-semibold hover:bg-[#FFFBF5]"
          >
            Buy Now — Cash on Delivery
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Reviews;
